import { supabase, STORAGE_BUCKETS } from '../lib/supabase'
import { FileUploadResult } from '../types'

export const uploadComplaintImage = async (
  file: File,
  complaintId: string
): Promise<FileUploadResult> => {
  try {
    const fileExt = file.name.split('.').pop()
    const fileName = `${complaintId}-${Date.now()}.${fileExt}`
    const filePath = `complaints/${fileName}`

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.COMPLAINT_IMAGES)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) {
      throw error
    }

    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKETS.COMPLAINT_IMAGES)
      .getPublicUrl(data.path)

    return {
      url: publicUrl,
      path: data.path
    }
  } catch (error) {
    console.error('Image upload failed:', error)
    
    // In development, create a local placeholder URL
    if (process.env.NODE_ENV === 'development') {
      const reader = new FileReader()
      return new Promise((resolve, reject) => {
        reader.onload = () => {
          resolve({
            url: reader.result as string,
            path: `local/${complaintId}-${Date.now()}`
          })
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    }
    
    throw error
  }
}

export const uploadProofImage = async (
  file: File,
  complaintId: string
): Promise<FileUploadResult> => {
  const fileExt = file.name.split('.').pop()
  const fileName = `proof-${complaintId}-${Date.now()}.${fileExt}`
  const filePath = `proofs/${fileName}`

  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKETS.PROOF_IMAGES)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    })

  if (error) {
    throw error
  }

  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKETS.PROOF_IMAGES)
    .getPublicUrl(data.path)

  return {
    url: publicUrl,
    path: data.path
  }
}

export const deleteFile = async (bucket: string, path: string): Promise<void> => {
  const { error } = await supabase.storage
    .from(bucket)
    .remove([path])

  if (error) {
    throw error
  }
}

export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Please select a valid image file (JPEG, PNG, or WebP)'
    }
  }

  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024 // 5MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size must be less than 5MB'
    }
  }

  return { valid: true }
}

export const compressImage = (file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    const img = new Image()

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }

      // Set canvas dimensions
      canvas.width = width
      canvas.height = height

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height)
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            })
            resolve(compressedFile)
          } else {
            resolve(file)
          }
        },
        file.type,
        quality
      )
    }

    img.src = URL.createObjectURL(file)
  })
}
